import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const ServicesContainer = styled.div`
  padding: var(--spacing-xxl) 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
`;

const PageTitle = styled.h1`
  text-align: center;
  margin-bottom: var(--spacing-md);
  
  span {
    color: var(--accent-turquoise);
  }
`;

const IntroText = styled.p`
  text-align: center;
  max-width: 800px;
  margin: 0 auto var(--spacing-xl);
  font-size: 1.2rem;
  color: var(--text-light);
`;

const ServiceBlocksContainer = styled.div`
  display: grid;
  gap: var(--spacing-xl);
  margin-bottom: var(--spacing-xxl);
`;

const ServiceBlock = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceHeader = styled.div`
  padding: var(--spacing-lg);
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  border-bottom: ${props => props.isOpen ? `1px solid var(--grey-border)` : 'none'};
`;

const ServiceTitle = styled.h2`
  margin: 0;
  font-size: 1.8rem;
  
  span {
    color: ${props => props.colorAccent || 'var(--accent-turquoise)'};
  }
`;

const ServiceToggle = styled.span`
  font-size: 1.5rem;
  transition: transform 0.3s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0)'};
`;

const ServiceContent = styled.div`
  padding: ${props => props.isOpen ? 'var(--spacing-lg)' : '0 var(--spacing-lg)'};
  max-height: ${props => props.isOpen ? '1000px' : '0'};
  overflow: hidden;
  transition: all 0.5s ease;
`;

const ServiceDescription = styled.p`
  margin-bottom: var(--spacing-md);
`;

const ServiceTeaser = styled.p`
  font-weight: 600;
  color: var(--accent-lavender);
  margin-top: var(--spacing-md);
`;

const ServiceLink = styled(Link)`
  display: inline-block;
  margin-top: var(--spacing-md);
  color: var(--accent-turquoise);
  font-weight: 600;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const CTASection = styled.div`
  background-color: var(--secondary);
  padding: var(--spacing-xl);
  border-radius: 8px;
  text-align: center;
  margin-top: var(--spacing-xl);
`;

const CTATitle = styled.h2`
  margin-bottom: var(--spacing-md);
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: var(--accent-turquoise);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid var(--accent-turquoise);
  transition: all 0.3s ease;
  margin-top: var(--spacing-md);
  
  &:hover {
    background-color: transparent;
    color: var(--text);
  }
`;

const serviceData = [
  {
    id: 'travel-marketing',
    title: 'Travel Marketing',
    colorAccent: 'var(--accent-turquoise)',
    description: 'Data-driven marketing strategies that connect authentic travel experiences with the right audiences.',
    details: 'Our Travel Marketing services help small to medium travel operators craft compelling stories and implement effective campaigns. We focus on sustainable growth through email marketing, content strategy, social media, and strategic partnerships that respect your company values while driving real results.',
    teaser: 'Doubled inquiries for a safari outfit.',
    caseStudyId: 'kikooko',
    caseStudyName: 'Kikooko'
  },
  {
    id: 'trade-show-support',
    title: 'Trade Show Support',
    colorAccent: 'var(--accent-turquoise)',
    description: 'Comprehensive trade show solutions that maximize ROI and convert leads into bookings.',
    details: 'Our specialized Trade Show Support services handle everything from pre-show marketing collateral development to post-show lead nurturing. We create eye-catching booth materials, design effective contests and giveaways, implement lead management systems, and develop tailored follow-up strategies that produce measurable results.',
    teaser: 'Increased qualified leads by 40% at ITB Berlin.',
    caseStudyId: 'travel-marketing',
    caseStudyName: 'Trade Show Case Study'
  },
  {
    id: 'monthly-marketing-support',
    title: 'Monthly Marketing Support',
    colorAccent: 'var(--accent-lavender)',
    description: 'Affordable, consistent marketing support with transparent pricing and clear deliverables.',
    details: 'Our Monthly Marketing Support packages provide travel businesses with ongoing marketing expertise without the overhead of a full-time team. Starting at just $495/month, our tiered packages include social media management, email marketing, SEO, content creation, and B2B outreach support, all specially designed for travel operators.',
    teaser: 'Professional marketing support from $495/month.',
    caseStudyId: 'monthly-support',
    caseStudyName: 'Monthly Support'
  },
  {
    id: 'tourism-development',
    title: 'Tourism Development',
    colorAccent: 'var(--accent-lavender)',
    description: 'Building bridges between local operators and global markets through sustainable, community-centered approaches.',
    details: 'Our Tourism Development work empowers communities and small operators to meaningfully connect with global markets. We design familiarization trips, create partnership strategies, and develop programs that support economic growth while preserving local culture and environment.',
    teaser: 'Linked local operators to 8 global markets.',
    caseStudyId: 'eswatini',
    caseStudyName: 'Eswatini'
  },
  {
    id: 'digital-tools',
    title: 'Digital Tools',
    colorAccent: 'var(--accent-blue)',
    description: 'Custom software solutions that save time and enhance customer experiences for travel businesses.',
    details: 'Our Digital Tools are built specifically for the needs of small to medium travel operators. From itinerary builders to marketing automation tools, we create accessible technology that makes running your travel business more efficient without losing the human touch that makes your offerings special.',
    teaser: 'Cut planning time by 60%.',
    caseStudyId: 'tool-example',
    caseStudyName: 'Itinerary Generator'
  }
];

const Services = () => {
  const [openSections, setOpenSections] = useState({
    'travel-marketing': false,
    'trade-show-support': false,
    'monthly-marketing-support': false,
    'tourism-development': false,
    'digital-tools': false
  });

  const toggleSection = (id) => {
    setOpenSections({
      ...openSections,
      [id]: !openSections[id]
    });
  };

  return (
    <ServicesContainer>
      <Container>
        <PageTitle>Our <span>Services</span></PageTitle>
        <IntroText>At Passport Creative, we're dedicated to empowering small to medium travel operators with solutions that drive growth while staying true to their mission. We believe in sustainable tourism that benefits local communities and creates meaningful connections.</IntroText>
        
        <ServiceBlocksContainer>
          {serviceData.map((service) => (
            <ServiceBlock key={service.id}>
              <ServiceHeader 
                onClick={() => toggleSection(service.id)}
                isOpen={openSections[service.id]}
              >
                <ServiceTitle colorAccent={service.colorAccent}>
                  <span>{service.title}</span>
                </ServiceTitle>
                <ServiceToggle isOpen={openSections[service.id]}>
                  ▼
                </ServiceToggle>
              </ServiceHeader>
              
              <ServiceContent isOpen={openSections[service.id]}>
                <ServiceDescription>{service.description}</ServiceDescription>
                <p>{service.details}</p>
                <ServiceTeaser>{service.teaser}</ServiceTeaser>
                <ServiceLink to={`/lab-results/${service.caseStudyId}`}>
                  View {service.caseStudyName} Case Study
                </ServiceLink>
              </ServiceContent>
            </ServiceBlock>
          ))}
        </ServiceBlocksContainer>
        
        <CTASection>
          <CTATitle>Ready to Experiment?</CTATitle>
          <p>Let's discuss how our services can help your travel business grow.</p>
          <CTAButton to="/contact">Let's Talk</CTAButton>
        </CTASection>
      </Container>
    </ServicesContainer>
  );
};

export default Services;